import { type MouseEvent, useState } from "react";

import clsx from "clsx";
import { twMerge } from "tailwind-merge";

import { GenericTooltip } from "@components/tooltip-container";
import { usePrettyLogEventPage } from "@components/v2/analytics";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { type SavedView } from "@features/sales-v3/hooks/use-saved-views-queries";
import { generateAnonBase64Hash } from "@utils/anonymous";
import { usePrefs } from "@utils/client-side-preferences";

import { useSalesSavedViews } from "../sales-saved-views-provider";
import { useSalesPageV3NextContext } from "../sales-v3-next-provider";

import { DeleteSavedViews } from "./delete-saved-views";
import { UpdateSavedViews } from "./update-saved-views";
import { isTemplateViewCheck } from "./utils";

const mayOpenNewTab = (value: number | null) => (event: MouseEvent) => {
  event.persist();

  if ((event.ctrlKey || event.metaKey || event.type === "auxclick") && value) {
    event.stopPropagation();

    const savedViewsUrl = new URL(window.location.href);
    savedViewsUrl.searchParams.set("savedViewId", value.toString());
    window.open(savedViewsUrl.href, "_blank");
    return true;
  }

  return false;
};

interface Props {
  savedView: SavedView;
  className?: string;
  isSelected?: boolean;
  isReordering?: boolean;
  isDefault?: boolean;
  isTemplate?: boolean;
  onClick?(): void;
}

export const SavedViewsMenuRow = ({
  savedView,
  className,
  isSelected = false,
  isReordering = false,
  isDefault = false,
  isTemplate = false,
  onClick,
}: Props) => {
  const { isDemoMode } = usePrefs();
  const prettyLogEventPage = usePrettyLogEventPage();
  const { selectSavedView } = useSalesPageV3NextContext();
  const { deleteSavedView, createHiddenSavedViewTemplate } =
    useSalesSavedViews();

  const [isDeleting, setIsDeleting] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  const isSelectable =
    !isDeleting && !isUpdating && !isReordering && !isSelected;

  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({
      id: savedView.id,
    });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const reorderProps = isReordering
    ? { ref: setNodeRef, style, ...attributes, ...listeners }
    : {};

  const onDelete = async () => {
    if (savedView.is_template) {
      await createHiddenSavedViewTemplate(savedView.id);
    } else {
      await deleteSavedView(savedView.id);
    }

    const additionalActionInfo = isTemplateView
      ? "Hide sales saved view template"
      : "Delete sales saved view";

    prettyLogEventPage({
      domain: "Saved Views",
      action: "onClick",
      additionalActionInfo,
      data: {
        pageContext: { savedView },
      },
    });

    setIsDeleting(false);
  };

  const isTemplateView = isTemplateViewCheck(savedView);

  return (
    <div
      onClick={() => {
        if (isSelectable) {
          prettyLogEventPage({
            domain: "Saved Views",
            action: "onClick",
            additionalActionInfo: "Select Saved View",
            data: {
              savedView,
            },
          });

          selectSavedView(savedView);

          onClick?.();
        }
      }}
      onAuxClick={(event: MouseEvent) => mayOpenNewTab(savedView.id)(event)}
      className={twMerge(
        className,
        " w-full h-10 px-4 flex group items-center bg-white",
        clsx({
          "cursor-auto": isDeleting || isUpdating,
          "cursor-pointer hover:bg-cosmic-4": isSelectable,
          "font-bold bg-cosmic-4": isSelected && !isReordering,
          "bg-mars-2": isDeleting,
          "border-t": isReordering,
          "border-none": !isReordering,
          "border-gray-2": true,
        }),
      )}
    >
      <UpdateSavedViews
        savedView={savedView}
        isUpdating={isUpdating}
        setIsUpdating={setIsUpdating}
      />
      <DeleteSavedViews
        isDeleting={isDeleting}
        cancelDelete={() => setIsDeleting(false)}
        onDelete={onDelete}
      />
      {!isDeleting && !isUpdating && (
        <div
          className="flex items-center justify-between w-full truncate"
          {...reorderProps}
        >
          <div className="truncate flex items-center">
            {isReordering && (
              <i className="fa-regular fa-sharp fa-grip-dots text-primary cursor-pointer mr-2" />
            )}
            <div className="truncate">
              <span className="truncate flex-1 w-full">
                {generateAnonBase64Hash({
                  value: savedView.name,
                  prefix: "quick-filter",
                  doAnon: isDemoMode && !savedView.is_template,
                })}
              </span>
              <div className="flex gap-1.5">
                {isDefault && (
                  <div className="text-xxs text-cosmic font-light">Default</div>
                )}
                {isDefault && isTemplate && (
                  <div className="text-xxs text-cosmic">&bull;</div>
                )}
                {isTemplate && (
                  <div className="text-xxs text-cosmic font-light">
                    Template
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className={clsx(isReordering && "hidden", "flex items-center")}>
            <GenericTooltip
              className={clsx("hidden", {
                "group-hover:block":
                  !isSelected && !isTemplateView && !savedView.is_default,
              })}
              noInfoCircle
              content="Edit"
              mouseEnterDelay={0.5}
            >
              <button
                className="w-6 h-6 rounded-full text-primary ml-2 hover:bg-white"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsUpdating(true);
                }}
              >
                <i className="fa-regular fa-sharp fa-pen text-xs" />
              </button>
            </GenericTooltip>
            <GenericTooltip
              className={clsx("hidden", { "group-hover:block": !isSelected })}
              noInfoCircle
              content="Delete"
              mouseEnterDelay={0.5}
            >
              <button
                className="w-6 h-6 rounded-full text-mars+2 ml-0.5 hover:bg-white"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsDeleting(true);
                }}
              >
                <i className="fa-regular fa-sharp fa-trash text-xs" />
              </button>
            </GenericTooltip>
          </div>
        </div>
      )}
    </div>
  );
};
